// src/features/campaigns/components/EmailResultados.tsx
// ============================================================================
// Resultados de una campaña de CORREO: entrega, aperturas y clics, leídos de
// /campaigns/:id/email-stats. Existe porque una campaña de correo terminada
// (done → 'archived') no tiene botón de detalle en la lista: la tarjeta es el
// ÚNICO lugar donde el dueño puede ver si su correo sirvió de algo.
//   · size="compact" → franja para la tarjeta de CampaignsList (anillo chico)
//   · size="full"    → panel para CampaignPreview (anillo grande + letra chica)
// Se auto-oculta (null) si la campaña no es de correo o aún no salió nada, así
// que el llamador solo cuida no pedirlo para campañas de WhatsApp (ahorro de red).
// La línea de clics se OCULTA si stats.clicks_tracked=false: el rastreo del botón
// existe desde el 2-ago-2026 y antes de eso un 0% es artefacto, no dato.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { MailCheck, MailOpen, MousePointerClick } from 'lucide-react';
import { campaignsApi, type EmailStats } from '../../../api/campaignsApi';

interface Props {
  campaignId: string;
  size?: 'compact' | 'full';
}

/** '2026-08-03 00:59' → '3 ago, 00:59' (texto plano del server; sin líos de TZ). */
const fechaCorta = (s: string | null): string | null => {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})$/);
  if (!m) return s;
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${Number(m[3])} ${meses[Number(m[2]) - 1]}, ${m[4]}`;
};

/** Anillo de apertura (meter): relleno = accent, pista = el mismo tono aclarado,
 *  para que el estado se lea en toda la circunferencia. El número va en tokens de
 *  texto (no en el color de la serie) y el trazo anima al montar. */
const Anillo: React.FC<{ pct: number; sizePx: number }> = ({ pct, sizePx }) => {
  const r = (sizePx - 8) / 2;                 // 8 = grosor del trazo (4 por lado)
  const circ = 2 * Math.PI * r;
  const meta = circ * (1 - Math.min(100, Math.max(0, pct)) / 100);
  const [offset, setOffset] = useState(circ); // arranca vacío y transiciona al valor
  const pintado = useRef(false);
  useEffect(() => {
    if (pintado.current) { setOffset(meta); return; }
    pintado.current = true;
    const t = requestAnimationFrame(() => requestAnimationFrame(() => setOffset(meta)));
    return () => cancelAnimationFrame(t);
  }, [meta]);
  const grande = sizePx >= 80;
  return (
    <div className="relative flex-shrink-0" style={{ width: sizePx, height: sizePx }} role="img"
      aria-label={`${pct}% de los correos abiertos`}>
      <svg width={sizePx} height={sizePx} className="-rotate-90">
        <circle cx={sizePx / 2} cy={sizePx / 2} r={r} fill="none" strokeWidth="4"
          className="stroke-accent/20" />
        <circle cx={sizePx / 2} cy={sizePx / 2} r={r} fill="none" strokeWidth="4"
          strokeLinecap="round" className="stroke-accent"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.22, 1, 0.36, 1)' }} />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center font-semibold text-content ${grande ? 'text-xl' : 'text-xs'}`}>
        {Math.round(pct)}%
      </span>
    </div>
  );
};

const EmailResultados: React.FC<Props> = ({ campaignId, size = 'compact' }) => {
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    campaignsApi.emailStats(campaignId)
      .then((s) => { if (vivo) setStats(s); })
      .catch(() => { /* sin métricas no se rompe la tarjeta: simplemente no se pintan */ })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [campaignId]);

  if (cargando) {
    return size === 'full'
      ? <div className="h-24 rounded-lg bg-skeleton animate-pulse" />
      : <div className="mt-2 h-8 w-64 max-w-full rounded-md bg-skeleton animate-pulse" />;
  }
  if (!stats || stats.sent <= 0) return null;

  if (size === 'compact') {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2">
        <div className="flex items-center gap-2.5">
          <Anillo pct={stats.open_rate} sizePx={40} />
          <div className="leading-tight">
            <p className="text-sm font-semibold text-content">{stats.opened} de {stats.sent} lo abrieron</p>
            <p className="text-[11px] text-content-3">Resultados del correo</p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-content-2" title={stats.failed > 0 ? `${stats.failed} no se pudieron entregar` : 'Todos los correos se entregaron'}>
          <MailCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
          {stats.failed > 0 ? `${stats.sent} entregados · ${stats.failed} fallidos` : `${stats.sent} entregados`}
        </span>
        {stats.clicks_tracked && (
          <span className="flex items-center gap-1.5 text-xs text-content-2" title="Clics en el botón de WhatsApp del correo">
            <MousePointerClick className="h-3.5 w-3.5 text-content-3" />
            {stats.clicked} {stats.clicked === 1 ? 'clic' : 'clics'} al botón
          </span>
        )}
        {stats.last_open_at && (
          <span className="text-[11px] text-content-3">Última apertura: {fechaCorta(stats.last_open_at)}</span>
        )}
      </div>
    );
  }

  // size === 'full' — panel del detalle
  return (
    <div className="bg-surface shadow rounded-lg overflow-hidden">
      <div className="px-4 py-4 sm:px-6 border-b border-line">
        <h3 className="text-base font-medium text-content flex items-center gap-2">
          <MailOpen className="h-5 w-5 text-accent" /> Resultados del correo
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Entrega */}
          <div className="rounded-lg border border-line bg-surface-2/50 p-4 flex flex-col items-center justify-center text-center gap-1">
            <MailCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-2xl font-semibold text-content">{stats.sent}</p>
            <p className="text-xs text-content-3">
              {stats.failed > 0 ? <>entregados · <span className="text-red-600 dark:text-red-400">{stats.failed} fallidos</span></> : 'entregados, ninguno falló'}
            </p>
          </div>
          {/* Apertura — la ficha estrella */}
          <div className="rounded-lg border border-accent/25 bg-accent/5 p-4 flex flex-col items-center justify-center text-center gap-2">
            <Anillo pct={stats.open_rate} sizePx={88} />
            <p className="text-xs text-content-3">
              <span className="font-medium text-content-2">{stats.opened} {stats.opened === 1 ? 'persona' : 'personas'}</span> abrieron el correo
            </p>
          </div>
          {/* Clics (solo si el rastreo existía cuando salió la campaña) */}
          {stats.clicks_tracked ? (
            <div className="rounded-lg border border-line bg-surface-2/50 p-4 flex flex-col items-center justify-center text-center gap-1">
              <MousePointerClick className="h-5 w-5 text-content-3" />
              <p className="text-2xl font-semibold text-content">{stats.clicked}</p>
              <p className="text-xs text-content-3">{stats.clicked === 1 ? 'clic' : 'clics'} en el botón de WhatsApp</p>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-line p-4 flex items-center justify-center text-center">
              <p className="text-xs text-content-3">Los clics se miden en campañas enviadas desde ago-2026.</p>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          {stats.last_open_at && (
            <p className="text-xs text-content-2">Última apertura: {fechaCorta(stats.last_open_at)}</p>
          )}
          <p className="text-[11px] text-content-3">
            Las aperturas se detectan al cargar las imágenes del correo: quien lee sin imágenes no cuenta, así que el número real es mayor.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailResultados;
